import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import moment from 'moment'

import styles from './styles.module.scss'

import { hideAssetForm } from '../../../store/asset/actions'
import { createAsset, updateAsset, getAsset } from '../../../api'
import useNotify from '../../../hooks/useNotify'

import { CrossIcon } from '../Icons'
import Input from '../../Core/Input/Input'
import Label from '../../Core/InputLabel'
import InputSelect from '../../Core/InputSelect'
import AssetPhotoUpload from './AssetPhotoUpload'
import AssetDocumentsUpload from './AssetDocumentsUpload'

const conditions = [
  {
    name: 'New',
    id: 'new'
  }, {
    name: 'Good',
    id: 'good'
  }, {
    name: 'Fair',
    id: 'fair'
  }, {
    name: 'Needs attensions',
    id: 'need_attentions'
  }, {
    name: 'Damaged',
    id: 'damaged'
  }, {
    name: 'Unsafe',
    id: 'unsafe'
  }
]

const pricingModels = [
  {
    id: 'daily',
    name: 'Daily'
  }, {
    id: 'weekly',
    name: 'Weekly'
  }, {
    id: 'monthly',
    name: 'Monthly'
  }, {
    id: 'yearly',
    name: 'Yearly'
  }, {
    id: 'fixed',
    name: 'Fixed'
  }
]

const initialForm = {
  name: '',
  code: '',
  category_id: null,
  ownership_id: null,
  employee_id: null,
  place_id: null,
  location: '',
  coordinates: '',
  supplier: '',
  purchase_date: null,
  warranty_expiration: null,
  purchase_price: '',
  replacement_value: '',
  notes: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  condition: '',
  description: '',
  documents: [],

  rental_supplier: '',
  rental_supplier_reference: '',
  rental_start_date: null,
  rental_end_date: null,
  pricing_model: '',
  rate: 0,
  penalty_per_day: 0,
  contract_amount: 0,
}

const createAssetData = (form) => {
  const formData = new FormData()

  formData.append('name', form.name)
  formData.append('code', form.code)

  formData.append('category_id', String(form.category_id))
  formData.append('ownership_id', String(form.ownership_id))
  if (form.employee_id) {
    formData.append('employee_id', String(form.employee_id))
  }
  
  if (form.place_id) {
    formData.append('place_id', String(form.place_id))
  }

  formData.append('location', form.location)
  formData.append('coordinates', form.coordinates)
  formData.append('supplier', form.supplier)

  if (form.purchase_date) {
    formData.append('purchase_date', form.purchase_date)
  }

  if (form.warranty_expiration) {
    formData.append('warranty_expiration', form.warranty_expiration)
  }

  formData.append('purchase_price',  String(form.purchase_price))

  formData.append('replacement_value', String(form.replacement_value))

  formData.append('notes', form.notes)
  formData.append('status', form.status)

  // Details
  formData.append('details[manufacturer]', form.details.manufacturer)

  formData.append('details[model]', form.details.model)

  formData.append('details[serial_number]', form.details.serial_number)

  formData.append('details[condition]', form.details.condition)

  formData.append('details[description]', form.details.description)

  if (form.details.photo) {
    formData.append('details[photo]', form.details.photo)
  }

  // Documents
  form.documents.forEach((document, index) => {
    formData.append(`documents[${index}][type]`, document.type)
    formData.append(`documents[${index}][name]`, document.name || document.file.name)
    if (document.file) {
      formData.append(`documents[${index}][file]`, document.file)
    }
    if (document.id) {
      formData.append(`documents[${index}][id]`, document.id)
    }
  })

  // Rental
  if (form.rental) {
    formData.append('rental[supplier]', form.rental.supplier)
    formData.append('rental[supplier_reference]', form.rental.supplier_reference)
    if (form.rental.rental_start_date) {
      formData.append('rental[rental_start_date]', form.rental.rental_start_date)
    }
    if (form.rental.rental_end_date) {
      formData.append('rental[rental_end_date]', form.rental.rental_end_date)
    }
    formData.append('rental[pricing_model]', form.rental.pricing_model)
    formData.append('rental[rate]', String(form.rental.rate))
    formData.append('rental[penalty_per_day]', String(form.rental.penalty_per_day))
    formData.append('rental[contract_amount]', String(form.rental.contract_amount))
  }
  return formData
}

const AssetForm = ({companyId, options}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const asset = useSelector(state => state.asset.form)
  const { show } = useNotify()

  const { categories, ownerships, employees, places } = useSelector(state => state.asset.options)

  const [showLayout, setShowLayout] = useState(false)
  
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState({name: null, code: null, category_id: null, ownership_id: null})

  const uploadPhotoRef = useRef(null)

  const selectedOwnership = ownerships?.find(ownership => ownership.id === form.ownership_id)
  const isRental = selectedOwnership?.code === 'rental'

  useEffect(() => {
    if (asset.show) {
      setForm(initialForm)
      setErrors({name: null, code: null, category_id: null, ownership_id: null})
      setTimeout(() => {
        setShowLayout(true)
      }, 100)
    } else {
      setShowLayout(false)
    }
  }, [asset.show])

  useEffect(() => {
    if (asset.data?.id) {
      getAsset(companyId, asset.data.id).then(res => {
        if (res) {
          setForm(prev => ({
            ...prev,
            name: res.name || '',
            code: res.code || '',
            category_id: res.category_id || null,
            ownership_id: res.ownership_id || null,
            employee_id: res.employee_id || null,
            place_id: res.place_id || null,
            location: res.location || '',
            coordinates: res.coordinates || '',
            supplier: res.supplier || '',
            purchase_date: res.purchase_date ? moment(res.purchase_date).format('YYYY-MM-DD') : null,
            warranty_expiration: res.warranty_expiration ? moment(res.warranty_expiration).format('YYYY-MM-DD') : null,
            purchase_price: res.purchase_price || '',
            replacement_value: res.replacement_value || '',
            notes: res.notes || '',
            manufacturer: res.details?.manufacturer || '',
            model: res.details?.model || '',
            serial_number: res.details?.serial_number || '',
            condition: res.details?.condition || '',
            description: res.details?.description || '',
            documents: res.documents || [],
            rental_supplier: res.rental_detail?.supplier || '',
            rental_supplier_reference: res.rental_detail?.supplier_reference || '',
            rental_start_date: res.rental_detail?.rental_start_date ? moment(res.rental_detail.rental_start_date).format('YYYY-MM-DD') : null,
            rental_end_date: res.rental_detail?.rental_end_date ? moment(res.rental_detail.rental_end_date).format('YYYY-MM-DD') : null,
            pricing_model: res.rental_detail?.pricing_model || '',
            rate: res.rental_detail?.rate || 0,
            penalty_per_day: res.rental_detail?.penalty_per_day || 0,
            contract_amount: res.rental_detail?.contract_amount || 0,
          }))

          if (res.details?.photo && uploadPhotoRef.current) {
            uploadPhotoRef.current.setFile(res.details.photo)
          } else {
            uploadPhotoRef.current.reset()
          }
        }
      })      
    }
  }, [asset.data?.id])

  useEffect(() => {
    if (asset.data) {
      setForm(prev => ({
        ...prev,
        ...asset.data,
      }))
    }
  }, [asset.data])

  const handleClose = () => {
    dispatch(hideAssetForm())
  }

  const handleChangeForm = (e) => {
    const { name, value } = e.target
    setForm(prevForm => ({
      ...prevForm,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }))
    }
  }

  const validateForm = () => {
    let valid = true
    const newErrors = {}
    if (!form.name) {
      newErrors.name = t('Asset name is required')
      valid = false
    }
    if (!form.code) {
      newErrors.code = t('Asset code is required')
      valid = false
    }
    if (!form.category_id) {
      newErrors.category_id = t('Category is required')
      valid = false
    }
    if (!form.ownership_id) {
      newErrors.ownership_id = t('Ownership is required')
      valid = false
    }
    setErrors(newErrors)
    return valid
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }
    const post = {
      name: form.name,
      code: form.code,
      category_id: form.category_id,
      ownership_id: form.ownership_id,
      employee_id: form.employee_id,
      place_id: form.place_id,
      location: form.location,
      coordinates: '',
      supplier: form.supplier,
      purchase_date: form.purchase_date,
      warranty_expiration: form.warranty_expiration,
      purchase_price: form.purchase_price,
      replacement_value: form.replacement_value,
      notes: form.notes,
      status: 'available',
      details: {
        photo: uploadPhotoRef.current?.getFile() || null,
        manufacturer: form.manufacturer,
        model: form.model,
        serial_number: form.serial_number,
        condition: form.condition,
        description: form.description,
      },
      documents: form.documents,
      rental: isRental ? {
        supplier: form.rental_supplier,
        supplier_reference: form.rental_supplier_reference,
        rental_start_date: form.rental_start_date,
        rental_end_date: form.rental_end_date,
        pricing_model: form.pricing_model,
        rate: form.rate,
        penalty_per_day: form.penalty_per_day,
        contract_amount: form.contract_amount,
      } : null,
    }
    const formData = createAssetData(post)
    if (asset.data.id) {
      const res = await updateAsset(companyId, asset.data.id, formData)
      if (res) {
        handleSuccess('update')
      }
      return
    }
    const res = await createAsset(companyId, formData)
    if (res) {
      handleSuccess('create')
    }
  }

  const handleSuccess = (type) => {
    window.dispatchEvent(
      new CustomEvent('asset-updated', {detail: {type: type}})
    )
    show(type === 'update' ? t('Asset updated successfully') : t('Asset created successfully'))
    handleClose()
  }

  return (
    <>
      {
        asset.show
          ? <div className={cn(styles.layout, {[styles.active]: showLayout})} onClick={handleClose} />
          : null
      }
      <div className={cn(styles.container, {[styles.active]: asset.show})}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>{t('New Asset')}</div>
          <button type="button" className={styles.closeButton} onClick={handleClose}>
            <CrossIcon />
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Basic information') }</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Asset name')}<span className={styles.requiredMark}>*</span></>} htmlFor='name' />
                <Input
                  id='name'
                  name='name'
                  type='text'
                  placeholder={t('Enter asset name')}
                  value={form.name}
                  onChange={handleChangeForm}
                  fullWidth
                  error={errors.name}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Asset code')}<span className={styles.requiredMark}>*</span></>} htmlFor='code' />
                <Input
                  id='code'
                  name='code'
                  type='text'
                  placeholder={t('Enter asset code')}
                  value={form.code}
                  onChange={handleChangeForm}
                  fullWidth
                  error={errors.code}
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Category')}<span className={styles.requiredMark}>*</span></>} htmlFor='category_id' />
                <InputSelect
                  id='category_id'
                  placeholder={t('Select category')}
                  options={categories}
                  value={form.category_id}
                  name='category_id'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  error={errors.category_id}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Ownership')}<span className={styles.requiredMark}>*</span></>} htmlFor='ownership_id' />
                <InputSelect
                  id='ownership_id'
                  placeholder={t('Select ownership')}
                  options={ownerships}
                  value={form.ownership_id}
                  name='ownership_id'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  error={errors.ownership_id}
                />
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Identity details') }</div>
            <div className={styles.formRow}>
              <AssetPhotoUpload companyId={companyId} ref={uploadPhotoRef} />
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Manufacturer')}</>} htmlFor='manufacturer' />
                <Input
                  id='manufacturer'
                  name='manufacturer'
                  type='text'
                  placeholder={t('Enter manufacturer')}
                  value={form.manufacturer}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Model')}</>} htmlFor='model' />
                <Input
                  id='model'
                  name='model'
                  type='text'
                  placeholder={t('Enter model')}
                  value={form.model}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Serial number')}</>} htmlFor='serial_number' />
                <Input
                  id='serial_number'
                  name='serial_number'
                  type='text'
                  placeholder={t('Enter serial number')}
                  value={form.serial_number}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Condition')}</>} htmlFor='condition' />
                <InputSelect
                  id='condition'
                  placeholder={t('Select condition')}
                  options={conditions}
                  value={form.condition}
                  name='condition'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Description')}</>} htmlFor='description' />
                <textarea
                  id='description'
                  name='description'
                  placeholder={t('Enter description')}
                  value={form.description}
                  onChange={handleChangeForm}
                  rows={4}
                  className={styles.textarea}
                />
              </div>
            </div>
          </div>
          {
            isRental
              ? (
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>{ t('Rental details') }</div>
                  <div className={styles.formRow}>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Rental supplier')}</>} htmlFor='rental_supplier' />
                      <Input
                        id='rental_supplier'
                        name='rental_supplier'
                        type='text'
                        placeholder={t('Enter rental supplier')}
                        value={form.rental_supplier}
                        onChange={handleChangeForm}
                        fullWidth
                      />
                    </div>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Rental supplier reference')}</>} htmlFor='rental_supplier_reference' />
                      <Input
                        id='rental_supplier_reference'
                        name='rental_supplier_reference'
                        type='text'
                        placeholder={t('Enter rental supplier reference')}
                        value={form.rental_supplier_reference}
                        onChange={handleChangeForm}
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Rental start date')}</>} htmlFor='rental_start_date' />
                      <Input
                        id='rental_start_date'
                        name='rental_start_date'
                        type='date'
                        placeholder={t('Enter rental start date')}
                        value={form.rental_start_date}
                        onChange={handleChangeForm}
                        fullWidth
                      />
                    </div>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Rental end date')}</>} htmlFor='rental_end_date' />
                      <Input
                        id='rental_end_date'
                        name='rental_end_date'
                        type='date'
                        placeholder={t('Enter rental end date')}
                        value={form.rental_end_date}
                        onChange={handleChangeForm}
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Pricing model')}</>} htmlFor='pricing_model' />
                      <InputSelect
                        id='pricing_model'
                        placeholder={t('Select pricing model')}
                        options={pricingModels}
                        value={form.pricing_model}
                        name='pricing_model'
                        onChange={handleChangeForm}
                        valueKey='id'
                        labelKey='name'
                      />
                    </div>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Rate')}</>} htmlFor='rate' />
                      <Input
                        id='rate'
                        name='rate'
                        type='number'
                        placeholder={t('Enter rate')}
                        value={form.rate}
                        onChange={handleChangeForm}
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Penalty per day')}</>} htmlFor='penalty_per_day' />
                      <Input
                        id='penalty_per_day'
                        name='penalty_per_day'
                        type='number'
                        placeholder={t('Enter penalty per day')}
                        value={form.penalty_per_day}
                        onChange={handleChangeForm}
                        fullWidth
                      />
                    </div>
                    <div className={styles.formItem}>
                      <Label text={<>{t('Contract amount')}</>} htmlFor='contract_amount' />
                      <Input
                        id='contract_amount'
                        name='contract_amount'
                        type='number'
                        placeholder={t('Enter contract amount')}
                        value={form.contract_amount}
                        onChange={handleChangeForm}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              )
              : null
          }
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Assignment') }</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Assignment to')}</>} htmlFor='employee_id' />
                <InputSelect
                  id='employee_id'
                  placeholder={t('Select employee')}
                  options={employees}
                  value={form.employee_id}
                  name='employee_id'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  // error={errors.customer_id}
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Project')}</>} htmlFor='place_id' />
                <InputSelect
                  id='place_id'
                  placeholder={t('Select project')}
                  options={places}
                  value={form.place_id}
                  name='place_id'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  // error={errors.customer_id}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Current location')}</>} htmlFor='location' />
                <Input
                  id='location'
                  name='location'
                  type='text'
                  placeholder={t('Enter location')}
                  value={form.location}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Purchase & financial') }</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Supplier')}</>} htmlFor='supplier' />
                <Input
                  id='supplier'
                  name='supplier'
                  type='text'
                  placeholder={t('Enter supplier')}
                  value={form.supplier}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Purchase date')}</>} htmlFor='purchase_date' />
                <Input
                  id='purchase_date'
                  name='purchase_date'
                  type='date'
                  placeholder={t('Enter purchase date')}
                  value={form.purchase_date}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Purchase price')}</>} htmlFor='purchase_price' />
                <Input
                  id='purchase_price'
                  name='purchase_price'
                  type='number'
                  placeholder={t('Enter purchase price')}
                  value={form.purchase_price}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Replacement value')}</>} htmlFor='replacement_value' />
                <Input
                  id='replacement_value'
                  name='replacement_value'
                  type='number'
                  placeholder={t('Enter replacement value')}
                  value={form.replacement_value}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Warranty expiration')}</>} htmlFor='warranty_expiration' />
                <Input
                  id='warranty_expiration'
                  name='warranty_expiration'
                  type='date'
                  placeholder={t('Enter warranty expiration date')}
                  value={form.warranty_expiration}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Documents') }</div>
            <AssetDocumentsUpload
              documents={form.documents}
              onChange={docs => setForm({...form, documents: docs})} />
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Internal notes') }</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <textarea
                  id='notes'
                  name='notes'
                  placeholder={t('Enter notes')}
                  value={form.notes}
                  onChange={handleChangeForm}
                  rows={4}
                  className={styles.textarea}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={handleClose}>
            { t('Cancel') }
          </button>
          <button type="button" className={styles.saveButton} onClick={handleSubmit}>
            { asset.data.id ? t('Update asset') : t('Create asset') }
          </button>
        </div>
      </div>
    </>
  )
}

export default AssetForm
